// Generate a state and reducer with the entity AuditInvocieSummaryDTO

import { AuditInvoiceSummaryDTO } from '@iota/iota-management-portal-v4-client';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    ApproveAuditInvoiceSummary,
    AuditInvoiceSummaryActionFailure,
    CreateAuditInvoiceSummaryNote,
    LoadAllAuditInvoiceSummaries,
    LoadAllAuditInvoiceSummariesFailure,
    LoadAllAuditInvoiceSummariesSuccess,
    LoadAuditInvoiceSummary,
    LoadAuditInvoiceSummaryFailure,
    LoadAuditInvoiceSummarySuccess,
    MarkAuditInvoiceSummaryDocumentAsRead,
    RejectAuditInvoiceSummary,
    RemoveAuditInvoiceSummaryNote
} from '../actions/audit-invoice-summary.actions';
import { AppState } from '../store';
import {
    AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER,
    AuditInvoiceSummaryFilterLabels
} from 'src/app/shared/constants-and-enums/audit-invoice-types';
import { getFilteredAuditInvoiceSummaries } from 'src/app/shared/utils';
import { CategoryFilterTab } from 'src/app/shared/components/category-filter-tabs/category-filter-tabs.component';

export interface AuditInvoiceSummaryState extends EntityState<AuditInvoiceSummaryDTO> {
    loading: boolean;
    error: any;

    actionLoading: boolean;
    actionError: any;

    selectedDateFrom: string | undefined;
    selectedDateTo: string | undefined;
}

function selectAuditInvoiceSummaryId(a: AuditInvoiceSummaryDTO): string {
    return a.auditInvoiceSummaryId;
}

function sortAuditInvoiceSummaries(a: AuditInvoiceSummaryDTO, b: AuditInvoiceSummaryDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const auditInvoiceSummaryAdapter: EntityAdapter<AuditInvoiceSummaryDTO> = createEntityAdapter<AuditInvoiceSummaryDTO>({
    selectId: selectAuditInvoiceSummaryId,
    sortComparer: sortAuditInvoiceSummaries
});

export const initialState: AuditInvoiceSummaryState = auditInvoiceSummaryAdapter.getInitialState({
    loading: false,
    error: undefined,

    actionLoading: false,
    actionError: undefined,

    selectedDateFrom: undefined,
    selectedDateTo: undefined
});

export const auditInvoiceSummaryReducer = createReducer(
    initialState,

    on(LoadAllAuditInvoiceSummaries, (state, { from, to }) => ({
        ...state,
        loading: true,
        selectedDateFrom: from,
        selectedDateTo: to
    })),
    on(LoadAuditInvoiceSummary, (state) => ({ ...state, loading: true })),
    on(LoadAllAuditInvoiceSummariesSuccess, (state, { auditInvoiceSummaries }) =>
        auditInvoiceSummaryAdapter.setAll(auditInvoiceSummaries, { ...state, loading: false })
    ),
    on(LoadAuditInvoiceSummarySuccess, (state, { auditInvoiceSummary }) =>
        auditInvoiceSummaryAdapter.upsertOne(auditInvoiceSummary, { ...state, loading: false, actionLoading: false })
    ),
    on(LoadAuditInvoiceSummaryFailure, LoadAllAuditInvoiceSummariesFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(
        CreateAuditInvoiceSummaryNote,
        RemoveAuditInvoiceSummaryNote,
        ApproveAuditInvoiceSummary,
        RejectAuditInvoiceSummary,
        MarkAuditInvoiceSummaryDocumentAsRead,
        (state) => ({
            ...state,
            actionError: undefined,
            actionLoading: true
        })
    ),
    on(AuditInvoiceSummaryActionFailure, (state, { error }) => ({ ...state, actionLoading: false, actionError: error }))
);

const { selectAll } = auditInvoiceSummaryAdapter.getSelectors();

export const auditInvoiceSummaryState = (state: AppState) => state.auditInvoiceSummaryState;
export const _auditInvoiceSummaries = createSelector(auditInvoiceSummaryState, selectAll);
export const auditInvoiceSummariesLoading = createSelector(auditInvoiceSummaryState, (state) => state.loading);
export const auditInvoiceSummariesError = createSelector(auditInvoiceSummaryState, (state) => state.error);
export const auditInvoiceSummaryActionLoading = createSelector(auditInvoiceSummaryState, (state) => state.actionLoading);
export const auditInvoiceSummaryActionError = createSelector(auditInvoiceSummaryState, (state) => state.actionError);
export const auditInvoiceSummarySelectedDateFrom = createSelector(auditInvoiceSummaryState, (state) => state.selectedDateFrom);
export const auditInvoiceSummarySelectedDateTo = createSelector(auditInvoiceSummaryState, (state) => state.selectedDateTo);

export const auditInvoiceSummaryOverviewFilterTabs = createSelector(_auditInvoiceSummaries, (auditInvoiceSummaries) => {
    const enumKeyAndValues = Object.values(AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER);
    const enumValues = enumKeyAndValues.filter((value) => isNaN(Number(value)));

    return enumValues?.map((key) => {
        const filteredAuditInvoiceSummaries = getFilteredAuditInvoiceSummaries(auditInvoiceSummaries, key);

        const result: CategoryFilterTab = {
            key,
            label: AuditInvoiceSummaryFilterLabels?.[key],
            count: filteredAuditInvoiceSummaries?.length
        };

        return result;
    });
});
